import * as Phaser from 'phaser';
import type { LayerRuntime } from './ParallaxLayerManager';
import type { LayerAmbient } from './sceneAssetSpec.schema';
import type { IWindProvider } from '../../ui/WindSystem';
import {
  readPrefersReducedMotion,
  getReducedMotionQuery,
  AMBIENT_REDUCED_MOTION_FACTOR,
  type ReducedMotionQuery,
} from './ambientOverlayState';

const PHI = (1 + Math.sqrt(5)) / 2;

function organicWave(t: number, freqHz: number, phaseRad: number): number {
  const primary = Math.sin(t * Math.PI * 2 * freqHz + phaseRad);
  const secondary = Math.sin(t * Math.PI * 2 * freqHz * PHI + phaseRad * 1.3) * 0.3;
  return primary + secondary;
}

interface Accum {
  dx: number;
  dy: number;
  dAlpha: number;
  dRotation: number;
}

function applyEffect(
  ambient: LayerAmbient,
  t: number,
  reducedFactor: number,
  windOffsetX: number,
  accum: Accum,
): void {
  const amp = ambient.amplitude * reducedFactor;
  const phase = ambient.phaseRad ?? 0;

  switch (ambient.kind) {
    case 'horizontal-sway':
    case 'parallax-drift': {
      accum.dx += organicWave(t, ambient.frequencyHz, phase) * amp + windOffsetX * amp * 0.5;
      break;
    }
    case 'vertical-sway': {
      accum.dy += organicWave(t, ambient.frequencyHz, phase) * amp;
      break;
    }
    case 'breathing': {
      accum.dy += organicWave(t, ambient.frequencyHz, phase) * amp;
      const alphaAmp = (ambient.secondaryAmplitude ?? amp * 0.1) * reducedFactor;
      accum.dAlpha += organicWave(t, ambient.frequencyHz * 0.8, phase + 0.5) * alphaAmp;
      break;
    }
    case 'rotate-sway': {
      accum.dRotation += organicWave(t, ambient.frequencyHz, phase) * amp;
      break;
    }
    case 'alpha-flicker': {
      accum.dAlpha += organicWave(t, ambient.frequencyHz, phase) * amp;
      break;
    }
    case 'linear-drift':
      break;
  }
}

export class OrganicMotionSystem {
  private layers: LayerRuntime[] = [];
  private windProvider: IWindProvider | null = null;
  private reducedMotion = false;
  private reducedMotionQuery: ReducedMotionQuery | null = null;
  private reducedMotionListener: ((ev: { matches: boolean }) => void) | null = null;
  private active = false;

  start(layers: LayerRuntime[]): void {
    this.layers = layers;
    this.active = true;
    this.reducedMotion = readPrefersReducedMotion();
    this.reducedMotionQuery = getReducedMotionQuery();
    if (this.reducedMotionQuery?.addEventListener) {
      this.reducedMotionListener = (ev) => { this.reducedMotion = ev.matches; };
      this.reducedMotionQuery.addEventListener('change', this.reducedMotionListener);
    }
  }

  setWindProvider(provider: IWindProvider): void {
    this.windProvider = provider;
  }

  tick(timeMs: number): void {
    if (!this.active) return;
    const t = timeMs / 1000;
    const reducedFactor = this.reducedMotion ? AMBIENT_REDUCED_MOTION_FACTOR : 1;

    const windIntensity = this.windProvider?.getWindIntensity() ?? 0;
    const windDir = this.windProvider?.getWindDirection() ?? Math.PI;
    const windOffsetX = Math.cos(windDir) * windIntensity;

    for (const layer of this.layers) {
      const motions = layer.spec.organicMotion;
      if (!motions || motions.length === 0) continue;

      const accum: Accum = { dx: 0, dy: 0, dAlpha: 0, dRotation: 0 };

      if (layer.spec.ambient) {
        applyEffect(layer.spec.ambient, t, reducedFactor, windOffsetX, accum);
      }

      for (const motion of motions) {
        applyEffect(motion, t, reducedFactor, windOffsetX, accum);
      }

      if (layer.image) {
        layer.image.x = layer.baseX + accum.dx;
        layer.image.y = layer.baseY + accum.dy;
        layer.image.rotation = layer.baseRotation + accum.dRotation;
        layer.image.setAlpha(Phaser.Math.Clamp(layer.baseAlpha + accum.dAlpha, 0, 1));
      } else if (layer.graphics) {
        layer.graphics.x = accum.dx;
        layer.graphics.y = accum.dy;
        layer.graphics.setAlpha(Phaser.Math.Clamp(layer.baseAlpha + accum.dAlpha, 0, 1));
      }
    }
  }

  pause(): void {
    this.active = false;
  }

  resume(): void {
    this.active = true;
  }

  destroy(): void {
    this.active = false;
    this.layers = [];
    this.windProvider = null;
    if (this.reducedMotionQuery?.removeEventListener && this.reducedMotionListener) {
      this.reducedMotionQuery.removeEventListener('change', this.reducedMotionListener);
    }
    this.reducedMotionQuery = null;
    this.reducedMotionListener = null;
  }
}
